import { Accessibility as AccessibilityIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useMemo } from "react";
import { IssueTable } from "../components/issue-table";
import { PageHeader } from "../components/page-header";
import { SearchBar } from "../components/search-bar";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useDebounce } from "../hooks/use-debounce";
import { useUrlFilters } from "../hooks/use-url-filters";
import { STATUS_COLORS } from "../theme/theme";
import type { Issue } from "../types.ts";

export default function IssuesPage() {
  const { data: issues, isLoading, refetch } = useApiQuery<Issue[]>("/issues");

  // URL-based filter state (persists across navigation)
  const [filters, setFilters] = useUrlFilters({
    search: "",
    selectedStatus: "open",
    showAccessibilityIssues: false,
  });

  const debouncedSearchQuery = useDebounce(filters.search, 300);

  const filteredIssues = useMemo(() => {
    let data = (issues ?? []).slice();

    if (debouncedSearchQuery) {
      const q = debouncedSearchQuery.toLowerCase();
      data = data.filter((it) => {
        const inTitle = it.title?.toLowerCase().includes(q);
        const inSlug = it.slug?.toLowerCase().includes(q);
        const inUrl = it.url?.toLowerCase().includes(q);
        const inTags = (it.tags ?? []).some((t) => t.toLowerCase().includes(q));
        return Boolean(inTitle || inSlug || inUrl || inTags);
      });
    }

    if (filters.selectedStatus !== "all") {
      data = data.filter((it) => {
        if (filters.selectedStatus === "open") {
          return it.issue_status === "open" || it.issue_status === "reopened";
        } else if (filters.selectedStatus === "resolved") {
          return it.issue_status === "closed" || it.issue_status === "resolved";
        }
        return true;
      });
    }

    // Filter accessibility/usability issues based on toggle
    if (!filters.showAccessibilityIssues) {
      data = data.filter((it) => {
        // Filter out issues that have a type starting with 'Axe '
        return !it.issue_type?.startsWith("axe_");
      });
    }

    return data;
  }, [issues, debouncedSearchQuery, filters.selectedStatus, filters.showAccessibilityIssues]);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <PageHeader title="Issues" subtitle="Monitor and manage detected issues across your tests">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
            <Box sx={{ flex: 1 }}>
              <SearchBar
                size="small"
                value={filters.search}
                onChange={(search) => setFilters({ search })}
                loading={isLoading}
                label="Search issues..."
                sx={{
                  width: "100%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "text.secondary",
                  },
                  "& .MuiOutlinedInput-input": { fontSize: "0.8rem" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "info.dark",
                  },
                }}
              />
            </Box>
            <Box>
              <ToggleButtonGroup
                value={filters.selectedStatus}
                onChange={(_, newValue: string) => {
                  if (newValue !== null) setFilters({ selectedStatus: newValue });
                }}
                size="small"
                exclusive
                sx={{
                  "& .MuiToggleButton-root": {
                    px: 2,
                    py: 0.5,
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    textTransform: "none",
                    borderColor: "divider",
                    color: "text.secondary",
                    "&.Mui-selected": {
                      backgroundColor: "info.dark",
                      color: "common.white",
                      borderColor: "info.dark",
                      "&:hover": {
                        backgroundColor: "info.dark",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "grey.100",
                      borderColor: "info.dark",
                    },
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="open">Open</ToggleButton>
                <ToggleButton value="resolved">Resolved</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>

          {/* Accessibility/Usability Issues Toggle - Top Right Corner */}
          <Box sx={{ ml: 2 }}>
            <Tooltip title="Show Usability Issues">
              <IconButton
                size="small"
                onClick={() =>
                  setFilters({
                    showAccessibilityIssues: !filters.showAccessibilityIssues,
                  })
                }
                sx={{
                  color: filters.showAccessibilityIssues ? "info.dark" : "text.secondary",
                  backgroundColor: filters.showAccessibilityIssues
                    ? STATUS_COLORS.info.bg
                    : "transparent",
                  border: "1px solid",
                  borderColor: filters.showAccessibilityIssues ? "info.dark" : "divider",
                  "&:hover": {
                    backgroundColor: filters.showAccessibilityIssues ? "info.light" : "grey.100",
                    borderColor: "info.dark",
                  },
                }}
              >
                <AccessibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </PageHeader>

      <IssueTable data={filteredIssues} isLoading={isLoading} onDelete={refetch} />
    </Box>
  );
}
